/**

 * Stories API client: list, get, create, view, set public (admin), delete.

 */



import { getApiUrl, apiFetch } from "./client";

import { isBrowserOffline } from "../utils/offline";

import {

  CACHE_KEYS,

  TTL,

  deleteCache,

  getCache,

  getCacheSync,

  setCache,

} from "../utils/appDataCache";
import { toUserMessage } from "../utils/userMessage";



let storiesListCache = null;

let storiesListCachedAt = 0;

let storiesListInFlight = null;

let canPostCache = null;

function isStoriesListCacheFresh() {
  return (
    storiesListCache &&
    storiesListCachedAt > 0 &&
    Date.now() - storiesListCachedAt < TTL.storiesList
  );
}

export function invalidateStoriesListCache() {
  storiesListCache = null;
  storiesListCachedAt = 0;
  storiesListInFlight = null;
  canPostCache = null;
  void deleteCache(CACHE_KEYS.storiesList);
  void deleteCache(CACHE_KEYS.storiesCanPost);
}

/** خواندن لیست استوری از کش بدون درخواست شبکه */
export function peekStoriesListSync({ allowStale = true } = {}) {
  const hit = getCacheSync(CACHE_KEYS.storiesList, {
    ttlMs: TTL.storiesList,
    allowStale,
  });
  if (!hit?.data) {
    return { payload: null, stale: true, cachedAt: 0 };
  }
  applyStoriesListFromCacheEntry(hit);
  return {
    payload: hit.data,
    stale: !!hit.stale,
    cachedAt: hit.cachedAt || storiesListCachedAt,
  };
}



/** Mark one story as seen in the in-memory + persisted list cache. */
function applyStorySeenToListCache(storyId) {
  const sid = Number(storyId);
  if (!sid || !storiesListCache?.data || !Array.isArray(storiesListCache.data)) {
    return false;
  }

  let changed = false;
  const data = storiesListCache.data.map((story) => {
    if (Number(story?.id) === sid && story.seen_by_viewer !== true) {
      changed = true;
      return { ...story, seen_by_viewer: true };
    }
    return story;
  });

  if (!changed) return false;

  storiesListCache = { ...storiesListCache, data };
  storiesListCachedAt = Date.now();
  void setCache(CACHE_KEYS.storiesList, storiesListCache);
  return true;
}



export function dispatchStoryListRefresh({ force = true } = {}) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("storyListRefresh", { detail: { force: !!force } })
  );
}



function applyStoriesListFromCacheEntry(entry) {
  if (!entry?.data) return null;
  storiesListCache = entry.data;
  storiesListCachedAt = entry.cachedAt || Date.now();
  return entry.data;
}



async function readStoriesListCache({ allowStale = false } = {}) {

  const hit = await getCache(CACHE_KEYS.storiesList, {

    ttlMs: TTL.storiesList,

    allowStale: allowStale || isBrowserOffline(),

  });

  return hit?.data ?? null;

}



async function readCanPostCache({ allowStale = false } = {}) {

  const hit = await getCache(CACHE_KEYS.storiesCanPost, {

    ttlMs: TTL.storiesCanPost,

    allowStale: allowStale || isBrowserOffline(),

  });

  if (hit?.data && typeof hit.data.canPost === "boolean") {

    return hit.data;

  }

  return null;

}

function normalizeStoryPostCapability(raw) {
  const canPost = raw?.can_post === true || raw?.canPost === true;
  const blocked = raw?.blocked === true;
  const canCreate =
    typeof raw?.can_create === "boolean"
      ? raw.can_create
      : typeof raw?.canCreate === "boolean"
        ? raw.canCreate
        : canPost && !blocked;
  const block = raw?.block && typeof raw.block === "object" ? raw.block : null;
  return { canPost, canCreate, blocked, block };
}



/**

 * @param {{ author_remote_id?: number }} [opts] - optional: fetch only stories by this author (for avatar entry)

 */

export async function fetchStories(opts = {}) {
  const force = opts.force === true;

  if (opts?.author_remote_id != null) {
    const authorId = Number(opts.author_remote_id);
    if (!force) {
      const fromList = Array.isArray(storiesListCache?.data)
        ? storiesListCache.data.filter((s) => Number(s?.author_remote_id) === authorId)
        : [];
      if (fromList.length) return { data: fromList };
      const peek = peekStoriesListSync({ allowStale: true });
      const cachedList = Array.isArray(peek.payload?.data) ? peek.payload.data : [];
      const filtered = cachedList.filter((s) => Number(s?.author_remote_id) === authorId);
      if (filtered.length) return { data: filtered };
    }

    const q = `?author_remote_id=${authorId}`;
    const res = await apiFetch(`/stories${q}`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(toUserMessage(data?.message, "خطا در دریافت استوری‌ها"));
    return data;
  }

  if (!force && isStoriesListCacheFresh()) {
    return storiesListCache;
  }

  if (!force) {
    const freshHit = getCacheSync(CACHE_KEYS.storiesList, {
      ttlMs: TTL.storiesList,
      allowStale: false,
    });
    if (freshHit?.data) {
      return applyStoriesListFromCacheEntry(freshHit);
    }
  }

  if (storiesListInFlight) return storiesListInFlight;



  const syncHit = getCacheSync(CACHE_KEYS.storiesList, {

    ttlMs: TTL.storiesList,

    allowStale: isBrowserOffline(),

  });

  if (syncHit?.data) {

    applyStoriesListFromCacheEntry(syncHit);

  }



  if (isBrowserOffline()) {

    const offlineData = syncHit?.data ?? (await readStoriesListCache({ allowStale: true }));

    if (offlineData) {

      storiesListCache = offlineData;

      return offlineData;

    }

    throw new Error("استوری‌ها در حالت آفلاین در دسترس نیستند.");

  }



  storiesListInFlight = apiFetch("/stories")

    .then(async (res) => {

      const data = await res.json().catch(() => ({}));

      if (!res.ok) throw new Error(toUserMessage(data?.message, "خطا در دریافت استوری‌ها"));

      storiesListCache = data;
      storiesListCachedAt = Date.now();
      await setCache(CACHE_KEYS.storiesList, data);

      return data;

    })

    .catch(async (err) => {

      const cached = await readStoriesListCache({ allowStale: true });

      if (cached) {

        storiesListCache = cached;

        return cached;

      }

      throw err;

    })

    .finally(() => {

      storiesListInFlight = null;

    });



  return storiesListInFlight;

}



/**

 * Builds { [remote_id]: { has_story, seen_by_me, story_id } } from stories list response.

 */

export function buildStoryAvatarStatesFromStories(stories) {

  if (!Array.isArray(stories) || stories.length === 0) return {};



  const byAuthor = {};

  for (const story of stories) {

    const aid = story?.author_remote_id;

    if (aid == null) continue;

    const key = Number(aid);

    if (!byAuthor[key]) byAuthor[key] = [];

    byAuthor[key].push(story);

  }



  const result = {};

  for (const [aid, authorStories] of Object.entries(byAuthor)) {

    const sorted = [...authorStories].sort(

      (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)

    );

    const firstUnseen = sorted.find((s) => s.seen_by_viewer === false);

    const entryStoryId = firstUnseen ? firstUnseen.id : sorted[0]?.id ?? null;

    result[aid] = {

      has_story: true,

      seen_by_me: authorStories.every((s) => s.seen_by_viewer === true),

      story_id: entryStoryId,

    };

  }

  return result;

}



/** Loads story ring states for avatars via GET /stories (not avatar-states). */

export async function loadStoryAvatarStates() {

  const res = await fetchStories();

  const list = Array.isArray(res?.data) ? res.data : [];

  return buildStoryAvatarStatesFromStories(list);

}



/** Normalizes GET /stories/:id JSON into a single story object. */

export function parseStoryResponse(res) {

  if (!res) return null;

  const candidate = res?.data ?? res;

  if (candidate?.id != null) return candidate;

  if (candidate?.data != null && candidate.data.id != null) return candidate.data;

  return null;

}



export async function fetchStory(id) {

  const res = await apiFetch(`/stories/${id}`);

  const data = await res.json().catch(() => ({}));

  if (!res.ok) throw new Error(toUserMessage(data?.message, "خطا در دریافت استوری"));

  return data;

}



export async function fetchStoryPostCapability() {
  if (canPostCache != null && typeof canPostCache === "object" && isBrowserOffline()) {
    return canPostCache;
  }

  const syncHit = getCacheSync(CACHE_KEYS.storiesCanPost, {
    ttlMs: TTL.storiesCanPost,
    allowStale: isBrowserOffline(),
  });
  if (syncHit?.data && typeof syncHit.data.canPost === "boolean") {
    const fromCache = normalizeStoryPostCapability(syncHit.data);
    canPostCache = fromCache;
    if (isBrowserOffline()) return fromCache;
  }

  if (isBrowserOffline()) {
    const cached = await readCanPostCache({ allowStale: true });
    if (cached != null) {
      const fromCache = normalizeStoryPostCapability(cached);
      canPostCache = fromCache;
      return fromCache;
    }
    return { canPost: false, canCreate: false, blocked: false, block: null };
  }

  try {
    const res = await apiFetch("/stories/can-post");
    const data = await res.json().catch(() => ({}));
    const payload = res.ok ? data?.data : null;
    const cap = normalizeStoryPostCapability(payload || {});
    canPostCache = cap;
    if (res.ok) {
      await setCache(CACHE_KEYS.storiesCanPost, {
        canPost: cap.canPost,
        canCreate: cap.canCreate,
        blocked: cap.blocked,
        block: cap.block,
        can_post: cap.canPost,
        can_create: cap.canCreate,
        timestamp: Date.now(),
      });
    }
    return cap;
  } catch {
    const cached = await readCanPostCache({ allowStale: true });
    if (cached != null) {
      const fromCache = normalizeStoryPostCapability(cached);
      canPostCache = fromCache;
      return fromCache;
    }
    return { canPost: false, canCreate: false, blocked: false, block: null };
  }
}

export async function canPostStory() {
  const cap = await fetchStoryPostCapability();
  return cap.canPost === true;
}

export async function createStoryPostBlock({ userRemoteId, reason, duration, deleteActiveStories = false }) {
  const res = await apiFetch("/stories/post-blocks", {
    method: "POST",
    body: JSON.stringify({
      user_remote_id: Number(userRemoteId),
      reason: String(reason || "").trim(),
      duration,
      delete_active_stories: deleteActiveStories === true,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(toUserMessage(data?.message, "خطا در بلاک کردن کاربر"));
  return data?.data ?? null;
}

export async function fetchStoryPostBlock(userRemoteId) {
  const res = await apiFetch(`/stories/post-blocks/${Number(userRemoteId)}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(toUserMessage(data?.message, "خطا در دریافت وضعیت بلاک"));
  return data?.data ?? { blocked: false, block: null };
}

export async function removeStoryPostBlock(userRemoteId) {
  const res = await apiFetch(`/stories/post-blocks/${Number(userRemoteId)}`, {
    method: "DELETE",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(toUserMessage(data?.message, "خطا در برداشتن بلاک"));
  return data?.data ?? { blocked: false };
}



/**

 * Create or replace story. Pass form data: text_blocks (JSON array), optional button_label, button_url, optional image File.

 */

export async function createStory({ text_blocks = [], button_label, button_url, image = null, background = 0 }) {

  const form = new FormData();

  form.append("text_blocks", JSON.stringify(text_blocks));

  if (button_label != null) form.append("button_label", button_label);

  if (button_url != null) form.append("button_url", button_url);

  if (image instanceof File) form.append("image", image);

  form.append("background", String(Math.max(0, Math.min(7, Math.floor(Number(background)) || 0))));



  const res = await fetch(getApiUrl("/stories"), {

    method: "POST",

    credentials: "include",

    body: form,

  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const err = new Error(toUserMessage(data?.message, "خطا در ثبت استوری"));
    err.code = data?.code || null;
    err.block = data?.data?.block || null;
    throw err;
  }
  invalidateStoriesListCache();
  dispatchStoryListRefresh({ force: true });

  return data?.data ?? null;

}



export async function recordStoryView(id) {

  const res = await apiFetch(`/stories/${id}/view`, { method: "POST" });

  if (res.status !== 204) {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(toUserMessage(data?.message, "خطا در ثبت بازدید"));
  }

  const updatedLocally = applyStorySeenToListCache(id);
  dispatchStoryListRefresh({ force: !updatedLocally });

}



/** Toggle like on story. Returns { liked: boolean }. */

export async function toggleStoryLike(id) {

  const res = await apiFetch(`/stories/${id}/like`, { method: "POST" });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) throw new Error(toUserMessage(data?.message, "خطا در لایک"));

  return data?.data ?? { liked: false };

}



/** List users who liked the story (author or admin). */

export async function fetchStoryLikers(id) {

  const res = await apiFetch(`/stories/${id}/likers`);

  const data = await res.json().catch(() => ({}));

  if (!res.ok) throw new Error(toUserMessage(data?.message, "خطا در دریافت لایک‌کنندگان"));

  return data?.data ?? [];

}



export async function setStoryVisibility(id, visibilityMode) {

  const body =

    visibilityMode && typeof visibilityMode === "string"

      ? { visibility_mode: visibilityMode }

      : {};

  const res = await apiFetch(`/stories/${id}`, {

    method: "PATCH",

    body: JSON.stringify(body),

  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) throw new Error(toUserMessage(data?.message, "خطا در به‌روزرسانی"));

  return data?.data ?? null;

}



export async function setStoryPublic(id, isPublic) {

  const res = await apiFetch(`/stories/${id}`, {

    method: "PATCH",

    body: JSON.stringify({ is_public: isPublic }),

  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) throw new Error(toUserMessage(data?.message, "خطا در به‌روزرسانی"));

  return data?.data ?? null;

}



export async function deleteStory(id) {

  const res = await apiFetch(`/stories/${id}`, { method: "DELETE" });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) throw new Error(toUserMessage(data?.message, "خطا در حذف استوری"));

  invalidateStoriesListCache();
  dispatchStoryListRefresh({ force: true });

  return data;

}


